import { useState } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Checkbox,
  Pagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  useUpdateQuizMutation,
  useDeleteQuizMutation,
  useRemoveQuestionFromQuizMutation,
  useAddQuestionToQuizMutation,
  useGetQuizParticipantsQuery,
  useAddQuizParticipantsMutation,
  useRemoveQuizParticipantsMutation,
  useGetMyClassesQuery,
  useGetClassroomStudentsQuery,
} from "../../../../../../app/quizApiSlice";
import { useGetQuestionsQuery } from "../../../../../../app/questionApiSlice";
import MathContent from "../../../../../customComponents/MathContent";
import { stripHtmlTags } from "../../../../../../helpers/htmlToText";
import { localToUTC, utcToDatetimeLocal } from "../../../../../../helpers/dateUtils";
import { selectCurrentUser } from "../../../../auth/authSlice";
import { useGetClassesPaginatedQuery } from "../../../../classroom/classApiSlice";

const ManageQuizModal = ({ open, onClose, quiz, onDeleted, onUpdated }) => {
  const [tab, setTab] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Role detection (same logic as NewQuiz)
  const currentUser = useSelector(selectCurrentUser);
  const userRoles = currentUser?.roles?.map((r) => r?.role?.name).filter(Boolean) || [];
  const isTeacher = userRoles.includes("teacher");
  const isExaminer = userRoles.includes("examiner");
  // Examiner always fetches all classes, regardless of other roles
  const useAllClasses = isExaminer;

  // Settings state
  const [dueAt, setDueAt] = useState(utcToDatetimeLocal(quiz?.due_at));
  const [expiresAt, setExpiresAt] = useState(utcToDatetimeLocal(quiz?.expires_at));
  const [duration, setDuration] = useState(quiz?.duration || "");
  const [retakes, setRetakes] = useState(quiz?.retakes ?? 1);

  // Add questions state
  const [addView, setAddView] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionPage, setQuestionPage] = useState(1);

  // Participants state
  const [addParticipantsView, setAddParticipantsView] = useState(false);
  const [selectedClassroomId, setSelectedClassroomId] = useState("");
  const [participantsToRemove, setParticipantsToRemove] = useState([]);
  const [studentsToAdd, setStudentsToAdd] = useState([]);

  // Mutations
  const [updateQuiz, { isLoading: isUpdating }] = useUpdateQuizMutation();
  const [deleteQuiz, { isLoading: isDeleting }] = useDeleteQuizMutation();
  const [removeQuestion, { isLoading: isRemoving }] = useRemoveQuestionFromQuizMutation();
  const [addQuestion, { isLoading: isAdding }] = useAddQuestionToQuizMutation();
  const [addParticipants, { isLoading: isAddingParticipants }] = useAddQuizParticipantsMutation();
  const [removeParticipants, { isLoading: isRemovingParticipants }] = useRemoveQuizParticipantsMutation();

  // Participants queries (lazy — only fetch when on Participants tab)
  const { data: participantsData, isLoading: isLoadingParticipants } = useGetQuizParticipantsQuery(
    quiz?.id,
    { skip: !quiz?.id || tab !== 2 }
  );
  const { data: myClassesRaw = [] } = useGetMyClassesQuery(undefined, { skip: tab !== 2 || useAllClasses });
  const { data: allClassesData } = useGetClassesPaginatedQuery(
    { page: 1, per_page: 200 },
    { skip: tab !== 2 || !useAllClasses }
  );
  const { data: classroomStudentsRaw = [] } = useGetClassroomStudentsQuery(
    { classroom_id: selectedClassroomId },
    { skip: !selectedClassroomId || tab !== 2 }
  );

  const participants = participantsData?.participants || [];
  const existingParticipantIds = new Set(participants.map((p) => p.id));
  const allMyClasses = useAllClasses ? (allClassesData?.classrooms || []) : myClassesRaw;
  const levelClasses = allMyClasses.filter((c) => c.level === quiz?.level);
  const classesToShow = levelClasses.length > 0 ? levelClasses : allMyClasses;
  const addableStudents = classroomStudentsRaw.filter((s) => !existingParticipantIds.has(s.user_id));

  // Fetch available questions for "Add Questions" view
  const { data: questionsData, isLoading: isLoadingQuestions } = useGetQuestionsQuery(
    {
      page: questionPage,
      per_page: 10,
      subject: quiz?.subject || null,
      level: quiz?.level || null,
    },
    { skip: !addView }
  );

  const existingQuestionIds = new Set(quiz?.questions?.map((q) => q.id) || []);

  const handleUpdateSettings = async () => {
    try {
      const result = await updateQuiz({
        id: quiz.id,
        due_at: localToUTC(dueAt) || undefined,
        expires_at: localToUTC(expiresAt) || undefined,
        duration: duration ? Number(duration) : undefined,
        retakes: retakes !== "" ? Number(retakes) : undefined,
      }).unwrap();
      onUpdated?.(result.quiz);
      onClose();
    } catch (err) {
      console.error("Failed to update quiz:", err);
    }
  };

  const handleDeleteQuiz = async () => {
    try {
      await deleteQuiz({ id: quiz.id }).unwrap();
      onDeleted?.();
    } catch (err) {
      console.error("Failed to delete quiz:", err);
    }
  };

  const handleRemoveQuestion = async (questionId) => {
    try {
      const result = await removeQuestion({ quizId: quiz.id, questionId }).unwrap();
      onUpdated?.(result.quiz);
    } catch (err) {
      console.error("Failed to remove question:", err);
    }
  };

  const handleAddSelected = async () => {
    try {
      let lastResult;
      for (const qId of selectedQuestions) {
        lastResult = await addQuestion({ quizId: quiz.id, questionId: qId }).unwrap();
      }
      setSelectedQuestions([]);
      setAddView(false);
      if (lastResult?.quiz) onUpdated?.(lastResult.quiz);
    } catch (err) {
      console.error("Failed to add questions:", err);
    }
  };

  const toggleQuestion = (id) => {
    setSelectedQuestions((prev) =>
      prev.includes(id) ? prev.filter((q) => q !== id) : [...prev, id]
    );
  };

  const handleRemoveParticipants = async () => {
    try {
      await removeParticipants({ id: quiz.id, user_ids: participantsToRemove }).unwrap();
      setParticipantsToRemove([]);
    } catch (err) {
      console.error("Failed to remove participants:", err);
    }
  };

  const handleAddParticipants = async () => {
    try {
      await addParticipants({ id: quiz.id, user_ids: studentsToAdd }).unwrap();
      setStudentsToAdd([]);
      setAddParticipantsView(false);
      setSelectedClassroomId("");
    } catch (err) {
      console.error("Failed to add participants:", err);
    }
  };

  const toggleParticipantToRemove = (userId) => {
    setParticipantsToRemove((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleStudentToAdd = (userId) => {
    setStudentsToAdd((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const availableQuestions =
    questionsData?.questions?.filter((q) => !existingQuestionIds.has(q.id)) || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight={700}>
          Manage Quiz
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "text.primary" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => { setTab(v); setAddView(false); setAddParticipantsView(false); setParticipantsToRemove([]); setStudentsToAdd([]); setSelectedClassroomId(""); }} sx={{ px: 3 }}>
        <Tab label="Settings" />
        <Tab label="Questions" />
        <Tab label="Participants" />
        <Tab label="Delete" />
      </Tabs>
      <Divider />

      <DialogContent sx={{ minHeight: 350 }}>
        {/* SETTINGS TAB */}
        {tab === 0 && (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Schedule & Timing
            </Typography>
            <TextField
              label="Due Date"
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Expiry Date"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Duration (minutes)"
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              fullWidth
              inputProps={{ min: 1 }}
            />
            <TextField
              label="Retakes allowed"
              type="number"
              value={retakes}
              onChange={(e) => setRetakes(e.target.value)}
              fullWidth
              inputProps={{ min: 1 }}
              helperText="Number of times a student can take this quiz"
            />
          </Stack>
        )}

        {/* QUESTIONS TAB */}
        {tab === 1 && !addView && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={600}>
                Quiz Questions ({quiz?.questions?.length || 0})
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddView(true)}
                sx={{
                  color: "inherit",
                  borderColor: "currentColor",
                }}
              >
                Add Questions
              </Button>
            </Stack>
            {quiz?.questions?.map((question, i) => (
              <Box
                key={question.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Typography
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <MathContent html={question.body} />
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveQuestion(question.id)}
                  disabled={isRemoving}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            {(!quiz?.questions || quiz.questions.length === 0) && (
              <Typography color="text.secondary" variant="body2" sx={{ textAlign: "center", py: 4 }}>
                No questions in this quiz.
              </Typography>
            )}
          </Stack>
        )}

        {/* ADD QUESTIONS VIEW */}
        {tab === 1 && addView && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={600}>
                Add Questions ({quiz?.subject} — {quiz?.level})
              </Typography>
              <Button size="small" onClick={() => { setAddView(false); setSelectedQuestions([]); }}>
                Back to list
              </Button>
            </Stack>
            {isLoadingQuestions ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : availableQuestions.length === 0 ? (
              <Typography color="text.secondary" variant="body2" sx={{ textAlign: "center", py: 4 }}>
                No additional questions available for this subject and level.
              </Typography>
            ) : (
              <>
                {availableQuestions.map((question) => (
                  <Box
                    key={question.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.5,
                      border: "1px solid",
                      borderColor: selectedQuestions.includes(question.id) ? "primary.main" : "divider",
                      borderRadius: 2,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    onClick={() => toggleQuestion(question.id)}
                  >
                    <Checkbox
                      checked={selectedQuestions.includes(question.id)}
                      size="small"
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {stripHtmlTags(question.body)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {question.topic}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {questionsData?.pagination?.pages > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                    <Pagination
                      count={questionsData.pagination.pages}
                      page={questionPage}
                      onChange={(_, p) => setQuestionPage(p)}
                      size="small"
                    />
                  </Box>
                )}
              </>
            )}
          </Stack>
        )}

        {/* PARTICIPANTS TAB — list view */}
        {tab === 2 && !addParticipantsView && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={600}>
                Participants ({participants.length})
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddParticipantsView(true)}
                sx={{ color: "inherit", borderColor: "currentColor" }}
              >
                Add from Class
              </Button>
            </Stack>
            {isLoadingParticipants ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : participants.length === 0 ? (
              <Typography color="text.secondary" variant="body2" sx={{ textAlign: "center", py: 4 }}>
                No participants assigned to this quiz.
              </Typography>
            ) : (
              participants.map((p) => (
                <Box
                  key={p.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    border: "1px solid",
                    borderColor: participantsToRemove.includes(p.id) ? "error.main" : "divider",
                    borderRadius: 2,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  onClick={() => toggleParticipantToRemove(p.id)}
                >
                  <Checkbox
                    checked={participantsToRemove.includes(p.id)}
                    size="small"
                    color="error"
                  />
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {(p.first_name?.[0] || p.username?.[0] || "?").toUpperCase()}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {p.first_name || p.last_name
                        ? `${p.first_name} ${p.last_name}`.trim()
                        : p.username}
                    </Typography>
                    {(p.first_name || p.last_name) && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {p.username}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Stack>
        )}

        {/* PARTICIPANTS TAB — add from class view */}
        {tab === 2 && addParticipantsView && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={600}>
                Add Participants from Class
              </Typography>
              <Button size="small" onClick={() => { setAddParticipantsView(false); setStudentsToAdd([]); setSelectedClassroomId(""); }}>
                Back to list
              </Button>
            </Stack>
            <TextField
              select
              label="Select Class"
              value={selectedClassroomId}
              onChange={(e) => { setSelectedClassroomId(e.target.value); setStudentsToAdd([]); }}
              fullWidth
              size="small"
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">— choose a class —</option>
              {classesToShow.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </TextField>
            {selectedClassroomId && addableStudents.length === 0 && (
              <Typography color="text.secondary" variant="body2" sx={{ textAlign: "center", py: 3 }}>
                All students in this class are already participants.
              </Typography>
            )}
            {addableStudents.map((s) => (
              <Box
                key={s.user_id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: studentsToAdd.includes(s.user_id) ? "primary.main" : "divider",
                  borderRadius: 2,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => toggleStudentToAdd(s.user_id)}
              >
                <Checkbox
                  checked={studentsToAdd.includes(s.user_id)}
                  size="small"
                />
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: "secondary.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {(s.first_name?.[0] || s.user?.username?.[0] || "?").toUpperCase()}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {s.first_name || s.last_name
                      ? `${s.first_name} ${s.last_name}`.trim()
                      : s.user?.username}
                  </Typography>
                  {s.reg_number && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {s.reg_number}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        )}

        {/* DELETE TAB */}
        {tab === 3 && (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box
              sx={{
                p: 3,
                border: "1px solid",
                borderColor: "error.light",
                borderRadius: 2,
                bgcolor: "error.50",
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} color="error">
                Delete Quiz
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This action cannot be undone. All quiz data including student submissions will become inaccessible.
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
                <Checkbox
                  checked={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2">
                  I understand this action is irreversible
                </Typography>
              </Stack>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteQuiz}
                disabled={!confirmDelete || isDeleting}
                sx={{ mt: 2 }}
              >
                {isDeleting ? "Deleting..." : "Delete Quiz"}
              </Button>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        {tab === 0 && (
          <Button
            variant="contained"
            onClick={handleUpdateSettings}
            disabled={isUpdating}
          >
            {isUpdating ? "Saving..." : "Save Changes"}
          </Button>
        )}
        {tab === 1 && addView && selectedQuestions.length > 0 && (
          <Button
            variant="contained"
            onClick={handleAddSelected}
            disabled={isAdding}
          >
            {isAdding ? "Adding..." : `Add ${selectedQuestions.length} Question${selectedQuestions.length > 1 ? "s" : ""}`}
          </Button>
        )}
        {tab === 2 && !addParticipantsView && participantsToRemove.length > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveParticipants}
            disabled={isRemovingParticipants}
          >
            {isRemovingParticipants ? "Removing..." : `Remove ${participantsToRemove.length} Participant${participantsToRemove.length > 1 ? "s" : ""}`}
          </Button>
        )}
        {tab === 2 && addParticipantsView && studentsToAdd.length > 0 && (
          <Button
            variant="contained"
            onClick={handleAddParticipants}
            disabled={isAddingParticipants}
          >
            {isAddingParticipants ? "Adding..." : `Add ${studentsToAdd.length} Student${studentsToAdd.length > 1 ? "s" : ""}`}
          </Button>
        )}
        <Button variant="outlined" onClick={onClose} sx={{ color: "text.primary", borderColor: "text.primary" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageQuizModal;
